import React from 'react';
import { ArrowRight, Zap, MessageCircle, Target, TrendingUp } from 'lucide-react';

const solutionSteps = [
  {
    number: "1",
    icon: <Zap size={24} />,
    title: "Auto-Launch Campaigns",
    description: "One click turns listings into Facebook, Google & blog content."
  },
  {
    number: "2",
    icon: <MessageCircle size={24} />,
    title: "Instant WhatsApp Engagement",
    description: "AI chats qualify every portal lead in seconds—24/7."
  },
  {
    number: "3",
    icon: <Target size={24} />,
    title: "Smart Retargeting",
    description: "Re-engage past leads via personalised WhatsApp & email drip."
  },
  {
    number: "4",
    icon: <TrendingUp size={24} />,
    title: "Always Discoverable",
    description: "Blogs & socials updated automatically to boost SEO."
  }
];

const SolutionFlow = () => {
  return (
    <section id="solution" className="py-20 lg:py-28 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Our{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              End-to-End Solution
            </span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From lead capture to conversion, automate your entire marketing workflow with AI-powered intelligence
          </p>
        </div>
        
        {/* Desktop: Horizontal flow */}
        <div className="hidden lg:flex items-start justify-between space-x-8">
          {solutionSteps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex-1 text-center group">
                {/* Step card */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-700 rounded-2xl p-8 transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 border border-purple-100 dark:border-slate-600">
                  {/* Number badge */}
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-lg">
                    {step.number}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-16 h-16 bg-white dark:bg-slate-600 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-md text-purple-600 dark:text-purple-400">
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
              
              {index < solutionSteps.length - 1 && (
                <div className="flex items-center justify-center mt-16 flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <ArrowRight className="text-white" size={20} />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Mobile & Tablet: Vertical stacked flow */}
        <div className="lg:hidden space-y-8">
          {solutionSteps.map((step, index) => (
            <div key={index} className="flex items-start">
              {/* Left side - number and connector */}
              <div className="flex-shrink-0 mr-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                  {step.number}
                </div>
                {index < solutionSteps.length - 1 && (
                  <div className="w-px h-20 bg-gradient-to-b from-purple-600 to-blue-600 mx-auto mt-4 opacity-30"></div>
                )}
              </div>
              
              {/* Right side - content */}
              <div className="flex-1 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-700 rounded-2xl p-6 border border-purple-100 dark:border-slate-600">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-white dark:bg-slate-600 rounded-lg flex items-center justify-center mr-4 shadow-md text-purple-600 dark:text-purple-400">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-16 lg:mt-20">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Ready to Automate Your Success?
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Join hundreds of real estate professionals who've transformed their lead generation with our AI-powered platform.
            </p>
            <a 
              href="#signup" 
              className="inline-flex items-center px-8 py-4 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Start Your Free Trial
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionFlow;